import { useEffect } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon, MessageTextSquare01Icon } from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { GalleryNeedsReviewModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-needs-review-modal/gallery-needs-review-modal'
import { NeedsReviewSpaceImages } from '@/features/photoset/components/needs-review-space-images'
import { PhotosetSpaceElement } from '@/features/photoset/components/photoset-space-element'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { SubHeaderBanner } from '@/features/photoset/components/sub-header-banner'
import { useExpandPhotosetImage } from '@/features/photoset/hooks/use-expand-photoset-image'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE } from '@/features/photoset/queries/use-get-photoset-images-list'
import { useGetPhotosetSpaces } from '@/features/photoset/queries/use-get-photoset-spaces'
import { useUpdateStepToRetouchingRevisionUpload } from '@/features/photoset/queries/use-update-step-to-retouching-revision-upload'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetNeedsReviewStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const initPhotosetSpaces = usePhotosetStore(
		(state) => state.initPhotosetSpaces
	)

	// queries
	const photosetQuery = useGetPhotoset(
		{
			id: urlParams.id
		},
		{ enabled: false }
	)

	const {
		data: photosetSpaces,
		isFetching: isFetchingPhotosetSpaces,
		isError: isErrorPhotosetSpaces
	} = useGetPhotosetSpaces({
		urlParams: {
			photosetId: urlParams.id
		},
		params: {
			response_type: GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE,
			is_final_select: true
		}
	})

	// mutations
	const useUpdateStepToRetouchingRevisionUploadMutation =
		useUpdateStepToRetouchingRevisionUpload({
			onError: (error) => errorHandler(error),
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PHOTOSET]
				})
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.MANUAL_RETOUCHING_SPACES]
				})
			}
		})

	//init
	useEffect(() => {
		if (photosetSpaces) {
			initPhotosetSpaces(photosetSpaces)
		}
	}, [photosetSpaces, initPhotosetSpaces])

	// Handle gallery modal view
	const {
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		currentDetailSpaceIndex,
		currentDetailSpace,
		onExpandImage,
		onReduceImage
	} = useExpandPhotosetImage()

	return (
		<>
			<PageHero
				endSlot={<StepHeroControls hideSortControls />}
				subtitle={t('photoset:needs_review.subtitle')}
				title={t('photoset:needs_review.title')}
			/>

			{photosetQuery.data?.comment && (
				<SubHeaderBanner
					subtitle={photosetQuery.data.comment}
					title={t('photoset:needs_review.error_banner_title')}
					type={'blue'}
				/>
			)}

			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer>
					{isFetchingPhotosetSpaces &&
						[0, 1, 2].map((e) => (
							<PhotosetSpaceElement asSkeleton key={`${e}-id`} />
						))}
					{!(isFetchingPhotosetSpaces || isErrorPhotosetSpaces) &&
						photosetSpaces?.map(
							(photosetSpace, spaceIndex) =>
								photosetSpace.images.length > 0 && (
									<PhotosetSpaceElement
										endSlot={
											<NeedsReviewSpaceImages
												images={photosetSpace.images}
												onExpandImage={onExpandImage}
												spaceIndex={spaceIndex}
											/>
										}
										key={photosetSpace.id}
										spaceHeaderEndSlot={
											photosetSpace.comment && (
												<div className='flex gap-2 whitespace-nowrap [&>p]:max-w-[570px] [&>p]:overflow-hidden [&>p]:text-ellipsis [&>p]:text-neutral-900 [&>p]:text-sm [&>svg]:fill-blue-600'>
													<MessageTextSquare01Icon
														size={20}
													/>
													<p>
														{photosetSpace.comment}
													</p>
													<Tooltip
														content={
															<p>
																{
																	photosetSpace.comment
																}
															</p>
														}
														contentProps={{
															sideOffset: 8,
															align: 'center',
															side: 'right'
														}}
														variant='light'
													>
														<ButtonLegacy
															className='min-h-0 p-0'
															type='button'
															variant='tertiary'
														>
															{t(
																'photoset:needs_review.view_more'
															)}
														</ButtonLegacy>
													</Tooltip>
												</div>
											)
										}
										spaceName={photosetSpace.space.key_name}
									/>
								)
						)}
				</PhotosetStepBodyContainer>
				<PageFooter
					endSlot={
						<ButtonBrandLegacy
							disabled={
								useUpdateStepToRetouchingRevisionUploadMutation.isPending
							}
							onClick={() =>
								useUpdateStepToRetouchingRevisionUploadMutation.mutate(
									urlParams.id
								)
							}
						>
							{t('photoset:needs_review.cta')}
							<ArrowRightIcon />
						</ButtonBrandLegacy>
					}
				/>
			</PhotosetStepWrapper>

			{currentDetailImageIndex !== null &&
				currentDetailSpaceIndex !== null &&
				currentDetailSpace && (
					<GalleryNeedsReviewModal
						currentDetailImageIndex={currentDetailImageIndex}
						currentSpaceName={currentDetailSpace.space.key_name}
						onClose={onReduceImage}
						selectableImages={currentDetailSpace.images}
						setCurrentDetailImageIndex={setCurrentDetailImageIndex}
					/>
				)}
		</>
	)
}
